import Decimal from 'decimal.js';
import { escapeCsvField as escapeDiagField } from './csvExport';
import { nativeCroEurPerUnit } from './cryptoPrices';
import { convertUsdToEur } from './eurFx';
import {
  groupCronosByRef,
  isUnknownCrc20AwaitingRatio,
  WCRO_CONTRACT_LOWER,
} from './croSwapRatioPricing';
import type { LedgerEvent, ProceedsDerivation, ValuationFlag } from './types';

const STABLE_USD = new Set(['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD']);

export type CronosProceedsDerivationStats = {
  swapLikeTxCount: number;
  disposalProceedsFromReceivedAsset: number;
  disposalProceedsFromOutgoingFallback: number;
  /** USDC/USDT/… acquisition rows priced with Frankfurter so `sumAcquisitionEur` can include them. */
  stableAcquisitionsPricedForSwap: number;
  /**
   * After disposal legs are EUR-priced, CRC-20 **acquisition** rows matched to same-tx stable/CRO
   * **disposals** (buy-side) receive a share of that EUR for FIFO cost basis.
   */
  tokenAcquisitionsFromPaidDisposals: number;
  zeroProceedsDisposalRows: number;
  zeroProceedsByAsset: Record<string, number>;
  zeroProceedsByInterpretation: Record<string, number>;
  /** Rows still unresolved after pricing (typically CRC-20 with no EUR path). */
  unresolvedZeroEurTokenRows: number;
};

function mergeFlags(ev: LedgerEvent, extra: string[]): void {
  const cur = ev.reviewFlags ?? [];
  ev.reviewFlags = Array.from(new Set([...cur, ...extra]));
}

function isSwapFlowParticipant(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos') return false;
  if (ev.kind === 'internal_transfer') return false;
  const c = ev.classification;
  if (
    c === 'INTERNAL_TRANSFER' ||
    c === 'SELF_TRANSFER' ||
    c === 'APPROVAL' ||
    c === 'IGNORE_NON_TAX'
  ) {
    return false;
  }
  return true;
}

/**
 * Economic swap-like: same tx hash has at least one taxable disposal and one acquisition
 * from owned-wallet flow (not address allowlists).
 */
export function detectSwapLikeByFlow(siblings: LedgerEvent[]): boolean {
  const parts = siblings.filter(isSwapFlowParticipant);
  return (
    parts.some((e) => e.kind === 'disposal') &&
    parts.some((e) => e.kind === 'acquisition')
  );
}

function sumAcquisitionEur(siblings: LedgerEvent[]): Decimal {
  let s = new Decimal(0);
  for (const ev of siblings) {
    if (!isSwapFlowParticipant(ev) || ev.kind !== 'acquisition') continue;
    const v = ev.eurValue;
    if (v != null && v.gt(0)) s = s.plus(v);
  }
  return s;
}

/** Incoming CRO / WCRO (wrapped) leg of a swap — same economic unit as native CRO for pricing. */
function isCroLikeAcquisitionLeg(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind !== 'acquisition') return false;
  const sym = ev.asset.toUpperCase();
  if (sym === 'CRO' || sym === 'WCRO') return true;
  const h = ev.cronosPriceHint;
  if (
    h?.mode === 'crc20' &&
    h.contractAddress.toLowerCase() === WCRO_CONTRACT_LOWER
  ) {
    return true;
  }
  return false;
}

/**
 * Router / multicall swaps: the WCRO→wallet leg can still have `eurValue` null here if the main CRO anchor
 * pass did not attach EUR (ordering, duplicate legs, or hint edge cases). Without this, `sumAcquisitionEur`
 * stays 0, swap proceeds are never allocated to the outgoing ERC-20 (e.g. CAW), and only stable/CRO outgoing
 * fallbacks apply — leaving meme-token disposals at 0. Price CRO/WCRO acquisitions using the same bundled
 * CRO/EUR path as the rest of the pipeline. (Log-based receipt parsing could add redundancy later.)
 */
async function ensureCroLikeAcquisitionsPricedForSwap(
  siblings: LedgerEvent[]
): Promise<void> {
  for (const ev of siblings) {
    if (!isSwapFlowParticipant(ev) || ev.kind !== 'acquisition') continue;
    if (ev.eurValue != null && ev.eurValue.gt(0)) continue;
    if (!isCroLikeAcquisitionLeg(ev)) continue;
    const date = new Date(ev.ts);
    const hint = ev.cronosPriceHint?.blockNumber;
    const unit = await nativeCroEurPerUnit(date, hint);
    if (unit != null && unit.gt(0)) {
      ev.eurValue = unit.mul(ev.amount);
      ev.cronosPriceHint = undefined;
      mergeFlags(ev, ['swap_group_cro_acquisition_priced']);
    }
  }
}

/**
 * DEX aggregators (Wolfswap, VVS router, etc.): net proceeds are often USDC/USDT to the wallet, but
 * `applyStablecoinAnchorsFromTxLegs` only priced **CRO** from stables — not the stable **acquisition**
 * row. Without EUR on that leg, `sumAcquisitionEur` stays 0 and meme-token disposals get no proceeds.
 */
async function ensureUsdStableAcquisitionsPricedForSwap(
  siblings: LedgerEvent[],
  stats: CronosProceedsDerivationStats
): Promise<void> {
  for (const ev of siblings) {
    if (!isSwapFlowParticipant(ev) || ev.kind !== 'acquisition') continue;
    if (ev.eurValue != null && ev.eurValue.gt(0)) continue;
    const sym = ev.asset.toUpperCase();
    if (!STABLE_USD.has(sym)) continue;
    const date = new Date(ev.ts);
    try {
      ev.eurValue = await convertUsdToEur(ev.amount, date);
      ev.cronosPriceHint = undefined;
      mergeFlags(ev, ['swap_group_stable_acquisition_priced', 'usd_stable_to_eur']);
      stats.stableAcquisitionsPricedForSwap++;
    } catch {
      /* Frankfurter failure — leave for other passes */
    }
  }
}

function disposalNeedsPrice(ev: LedgerEvent): boolean {
  return ev.eurValue == null || !ev.eurValue.gt(0);
}

async function estimateDisposalWeightEur(ev: LedgerEvent): Promise<Decimal> {
  const date = new Date(ev.ts);
  const sym = ev.asset.toUpperCase();
  if (STABLE_USD.has(sym)) {
    try {
      return await convertUsdToEur(ev.amount, date);
    } catch {
      return ev.amount.abs();
    }
  }
  if (sym === 'CRO' || sym === 'WCRO') {
    const hint = ev.cronosPriceHint?.blockNumber;
    const unit = await nativeCroEurPerUnit(date, hint);
    if (unit != null && unit.gt(0)) return unit.mul(ev.amount);
    return ev.amount.abs();
  }
  if (ev.eurValue != null && ev.eurValue.gt(0)) return ev.eurValue;
  return new Decimal(1);
}

function tagDisposalProceeds(
  ev: LedgerEvent,
  derivation: ProceedsDerivation,
  flag: ValuationFlag
): void {
  ev.proceedsDerivation = derivation;
  ev.valuationFlag = flag;
  if (derivation === 'received_asset') {
    mergeFlags(ev, ['swap_flow_proceeds', 'priced_from_received_asset_leg']);
  } else if (derivation === 'outgoing_asset_fallback') {
    mergeFlags(ev, ['outgoing_asset_proceeds_fallback', 'priced_from_outgoing_leg']);
  }
}

/**
 * Priority A: allocate total incoming EUR (same tx) across disposal legs.
 * Priority B (later): outgoing stable / CRO FMV when still missing.
 */
export async function applyCronosSwapProceedsDerivation(
  working: LedgerEvent[]
): Promise<CronosProceedsDerivationStats> {
  const stats: CronosProceedsDerivationStats = {
    swapLikeTxCount: 0,
    disposalProceedsFromReceivedAsset: 0,
    disposalProceedsFromOutgoingFallback: 0,
    stableAcquisitionsPricedForSwap: 0,
    tokenAcquisitionsFromPaidDisposals: 0,
    zeroProceedsDisposalRows: 0,
    zeroProceedsByAsset: {},
    zeroProceedsByInterpretation: {},
    unresolvedZeroEurTokenRows: 0,
  };

  const byRef = groupCronosByRef(working);

  for (const [, sibs] of Array.from(byRef.entries())) {
    if (!detectSwapLikeByFlow(sibs)) continue;
    stats.swapLikeTxCount++;

    await ensureCroLikeAcquisitionsPricedForSwap(sibs);
    await ensureUsdStableAcquisitionsPricedForSwap(sibs, stats);

    const disposals = sibs.filter(
      (e) => isSwapFlowParticipant(e) && e.kind === 'disposal'
    );
    const totalIn = sumAcquisitionEur(sibs);
    const targets = disposals.filter(disposalNeedsPrice);
    if (!totalIn.gt(0) || targets.length === 0) continue;

    if (targets.length === 1) {
      const d = targets[0]!;
      d.eurValue = totalIn;
      tagDisposalProceeds(d, 'received_asset', 'normal');
      d.cronosPriceHint = undefined;
      stats.disposalProceedsFromReceivedAsset++;
      continue;
    }

    const weights = await Promise.all(targets.map((d) => estimateDisposalWeightEur(d)));
    let sumW = weights.reduce((a, b) => a.plus(b), new Decimal(0));
    if (!sumW.gt(0)) {
      const share = totalIn.div(targets.length);
      for (const d of targets) {
        d.eurValue = share;
        tagDisposalProceeds(d, 'received_asset', 'normal');
        d.cronosPriceHint = undefined;
        stats.disposalProceedsFromReceivedAsset++;
      }
      continue;
    }

    for (let i = 0; i < targets.length; i++) {
      const d = targets[i]!;
      const w = weights[i]!;
      const share = totalIn.mul(w).div(sumW);
      d.eurValue = share;
      tagDisposalProceeds(d, 'received_asset', 'normal');
      d.cronosPriceHint = undefined;
      stats.disposalProceedsFromReceivedAsset++;
    }
  }

  for (const ev of working) {
    if (ev.source !== 'cronos' || ev.kind !== 'disposal') continue;
    if (!isSwapFlowParticipant(ev)) continue;
    if (!disposalNeedsPrice(ev)) continue;

    const date = new Date(ev.ts);
    const sym = ev.asset.toUpperCase();

    if (STABLE_USD.has(sym)) {
      try {
        ev.eurValue = await convertUsdToEur(ev.amount, date);
        tagDisposalProceeds(ev, 'outgoing_asset_fallback', 'fallback_outgoing_used');
        ev.cronosPriceHint = undefined;
        stats.disposalProceedsFromOutgoingFallback++;
      } catch {
        /* leave for unresolved */
      }
      continue;
    }

    if (sym === 'CRO' || sym === 'WCRO') {
      const unit = await nativeCroEurPerUnit(date, ev.cronosPriceHint?.blockNumber);
      if (unit != null && unit.gt(0)) {
        ev.eurValue = unit.mul(ev.amount);
        tagDisposalProceeds(ev, 'outgoing_asset_fallback', 'fallback_outgoing_used');
        ev.cronosPriceHint = undefined;
        stats.disposalProceedsFromOutgoingFallback++;
      }
    }
  }

  return stats;
}

/**
 * After all EUR passes: mark unknown CRC-20 rows at 0 EUR with review flags (existing behavior),
 * but skip rows that already have a valuation path.
 */
export function markUnresolvedCronosTokenZeroEur(ev: LedgerEvent): void {
  if (!isUnknownCrc20AwaitingRatio(ev)) return;
  ev.eurValue = new Decimal(0);
  ev.cronosPriceHint = undefined;
  ev.proceedsDerivation = 'none';
  ev.valuationFlag = 'missing_proceeds_review';
  mergeFlags(ev, [
    'unresolved_token_price',
    'missing_cost_basis',
    'no_cro_swap_ratio',
  ]);
  const extra =
    'EUR not derived from swap proceeds, stable/CRO fallback, or CRO-linked ratio; defaulted to 0 EUR pending review.';
  ev.explanation = ev.explanation ? `${ev.explanation} ${extra}` : extra;
}

/** Final audit counts after swap derivation + unresolved token zeroing. */
/** Compact CSV for pipeline / audit (swap proceeds pass). */
export function buildCronosProceedsDerivationDiagnosticsCsv(
  stats: CronosProceedsDerivationStats
): string {
  const rows: string[][] = [
    ['metric', 'value'],
    ['swap_like_tx_count', String(stats.swapLikeTxCount)],
    [
      'disposal_proceeds_from_received_asset',
      String(stats.disposalProceedsFromReceivedAsset),
    ],
    [
      'disposal_proceeds_from_outgoing_fallback',
      String(stats.disposalProceedsFromOutgoingFallback),
    ],
    [
      'stable_acquisitions_priced_for_swap',
      String(stats.stableAcquisitionsPricedForSwap),
    ],
    [
      'token_acquisitions_from_paid_disposals',
      String(stats.tokenAcquisitionsFromPaidDisposals),
    ],
    ['zero_proceeds_disposal_rows', String(stats.zeroProceedsDisposalRows)],
    [
      'unresolved_zero_eur_crc20_rows',
      String(stats.unresolvedZeroEurTokenRows),
    ],
  ];
  for (const [asset, n] of Object.entries(stats.zeroProceedsByAsset).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    rows.push([`zero_proceeds_asset_${asset}`, String(n)]);
  }
  for (const [tag, n] of Object.entries(stats.zeroProceedsByInterpretation).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    rows.push([`zero_proceeds_class_${tag}`, String(n)]);
  }
  return rows.map((r) => r.map(escapeDiagField).join(',')).join('\r\n');
}

export function buildCronosProceedsFinalStats(
  working: LedgerEvent[],
  partial: CronosProceedsDerivationStats,
  unresolvedTokenRowsZeroed: number
): CronosProceedsDerivationStats {
  const zeroProceedsByAsset: Record<string, number> = {};
  const zeroProceedsByInterpretation: Record<string, number> = {};
  let zeroProceedsDisposalRows = 0;

  for (const ev of working) {
    if (ev.source !== 'cronos' || ev.kind !== 'disposal') continue;
    if (!isSwapFlowParticipant(ev)) continue;
    const v = ev.eurValue;
    if (v != null && v.gt(0)) continue;
    zeroProceedsDisposalRows++;
    const asset = ev.asset.toUpperCase();
    zeroProceedsByAsset[asset] = (zeroProceedsByAsset[asset] ?? 0) + 1;
    const tag = ev.classification ?? 'unknown';
    zeroProceedsByInterpretation[tag] = (zeroProceedsByInterpretation[tag] ?? 0) + 1;
  }

  return {
    ...partial,
    zeroProceedsDisposalRows,
    zeroProceedsByAsset,
    zeroProceedsByInterpretation,
    unresolvedZeroEurTokenRows: unresolvedTokenRowsZeroed,
  };
}
